// Premium Propulse Flashcard — 3D floating cards with iridescent backs, progress dial,
// shuffle animation, swipe-direction hints, learning streak counter.
import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, RefreshCw, Volume2, VolumeX, Sparkles, Trophy, Zap, BookOpen } from 'lucide-react';
import { GameFrame, AnnouncementBanner, ScoreTile, playGameSound, GoldButton } from './shared/GameFX';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

export default function Flashcard({ game, onPlayed }) {
    const cards = game.config?.cards || [];
    const [idx, setIdx] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [results, setResults] = useState([]);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [done, setDone] = useState(false);
    const [muted, setMuted] = useState(false);
    const [announce, setAnnounce] = useState(null);
    const [bursts, setBursts] = useState([]);
    const [showFireworks, setShowFireworks] = useState(false);
    const burstIdRef = useRef(0);

    const triggerBurst = () => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        setBursts((b) => [...b, { id, count: 20, scale: 0.9 }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1400);
    };

    const announce_ = (text, tone = 'green') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1200);
    };

    const mark = async (gotIt) => {
        playGameSound(gotIt ? 'correct' : 'wrong', muted);
        const r = [...results, gotIt];
        setResults(r); setFlipped(false);
        if (gotIt) {
            const newStreak = streak + 1;
            setStreak(newStreak);
            setBestStreak((bs) => Math.max(bs, newStreak));
            triggerBurst();
            announce_(newStreak > 1 ? `🔥 ${newStreak} STREAK!` : '✅ Got it!', 'green');
        } else {
            setStreak(0);
            announce_('📖 Review', 'red');
        }
        if (idx >= cards.length - 1) {
            const score = Math.round((r.filter(Boolean).length / cards.length) * 100);
            const points = Math.max(5, Math.round(score * game.points_per_play / 100));
            setDone(true);
            if (score >= 80) {
                setShowFireworks(true);
                setTimeout(() => setShowFireworks(false), 3500);
                playGameSound('win', muted);
            }
            await api.post(`/games/${game.code}/submit`, { score, awarded_points: points, details: { correct: r.filter(Boolean).length, total: cards.length, bestStreak } });
            onPlayed && onPlayed();
        } else { setIdx(idx + 1); }
    };

    const reset = () => {
        setIdx(0); setFlipped(false); setResults([]);
        setStreak(0); setBestStreak(0); setDone(false); setShowFireworks(false);
    };

    if (done) {
        const correct = results.filter(Boolean).length;
        const pct = Math.round(correct / cards.length * 100);
        return (
            <GameFrame dataTestId="flashcard-done" orbs={[
                { color: '#10b981', x: '15%', y: '20%', size: 230, delay: 0 },
                { color: '#FFF200', x: '85%', y: '25%', size: 210, delay: 1.4 },
            ]}>
                <div className="text-center py-8">
                    <motion.div animate={{ y: [-4, 4, -4] }} transition={{ repeat: Infinity, duration: 1.6 }}>
                        <Trophy className="h-16 w-16 mx-auto text-amber-400 drop-shadow" />
                    </motion.div>
                    <div className="font-display font-extrabold text-4xl text-white mt-4">{pct}%</div>
                    <div className="text-sm text-slate-300 mt-1">You got <b className="text-emerald-300">{correct}</b> of {cards.length} flashcards · best streak <b className="text-amber-300">×{bestStreak}</b></div>
                    <div className="mt-6 flex justify-center">
                        <GoldButton onClick={reset} dataTestId="flashcard-restart">
                            <RefreshCw className="h-4 w-4" /> Play Again
                        </GoldButton>
                    </div>
                </div>
                {showFireworks && <Fireworks duration={4} density={1.4} />}
            </GameFrame>
        );
    }

    const c = cards[idx];
    const progressPct = ((idx) / cards.length) * 100;

    return (
        <GameFrame dataTestId="flashcard-game" orbs={[
            { color: '#3b82f6', x: '12%', y: '20%', size: 220, delay: 0 },
            { color: '#10b981', x: '85%', y: '18%', size: 200, delay: 1.4 },
            { color: '#a855f7', x: '50%', y: '90%', size: 240, delay: 2.8 },
        ]}>
            <Header muted={muted} onToggleMute={() => setMuted((m) => !m)} idx={idx} total={cards.length} streak={streak} bestStreak={bestStreak} />

            {/* Progress ring */}
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                <BookOpen className="h-3.5 w-3.5" /> Card {idx + 1} of {cards.length}
                <div className="flex-1 ml-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div animate={{ width: `${progressPct}%` }} className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" />
                </div>
            </div>

            {/* 3D floating card */}
            <div className="mt-4 relative h-80 sm:h-96 flex items-center justify-center" style={{ perspective: '1200px' }}>
                <AnimatePresence mode="wait">
                    <motion.div key={idx}
                        initial={{ opacity: 0, x: 80, rotateY: -30 }}
                        animate={{ opacity: 1, x: 0, rotateY: flipped ? 180 : 0, y: [0, -4, 0] }}
                        exit={{ opacity: 0, x: -80, rotateY: 30 }}
                        transition={{ duration: 0.5, y: { repeat: Infinity, duration: 3 } }}
                        className="relative w-full max-w-md h-full rounded-3xl cursor-pointer"
                        style={{ transformStyle: 'preserve-3d' }}
                        onClick={() => { setFlipped((f) => !f); playGameSound('flip', muted); }}
                    >
                        {/* Front */}
                        <div className="absolute inset-0 rounded-3xl overflow-hidden ring-2 ring-white/40 shadow-2xl"
                            style={{ backfaceVisibility: 'hidden' }}>
                            <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #6366f1 100%)' }} />
                            <motion.div animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }} transition={{ repeat: Infinity, duration: 8, ease: 'linear' }}
                                className="absolute inset-0 opacity-50"
                                style={{ background: 'linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)', backgroundSize: '200% 200%' }} />
                            <div className="relative h-full flex flex-col items-center justify-center text-center p-7">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-3 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />Question</div>
                                <div className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-snug" data-testid="flashcard-question">{c?.q}</div>
                                <div className="text-xs text-white/60 mt-6 absolute bottom-5 left-1/2 -translate-x-1/2">👆 Tap to reveal answer</div>
                            </div>
                            <div className="absolute inset-2 rounded-2xl border border-white/30 pointer-events-none" />
                        </div>
                        {/* Back */}
                        <div className="absolute inset-0 rounded-3xl overflow-hidden ring-2 ring-amber-300/60 shadow-2xl"
                            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
                            <div className="absolute inset-0"
                                style={{ background: 'linear-gradient(135deg, #FFD700 0%, #F59E0B 50%, #B45309 100%)' }} />
                            <div className="relative h-full flex flex-col items-center justify-center text-center p-7">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-900/80 mb-3 inline-flex items-center gap-1"><Zap className="h-3 w-3" />Answer</div>
                                <div className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 leading-snug" data-testid="flashcard-answer">{c?.a}</div>
                                <div className="text-xs text-slate-800/70 mt-4">Tap again to flip back</div>
                            </div>
                            <div className="absolute inset-2 rounded-2xl border border-slate-900/30 pointer-events-none" />
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 max-w-md mx-auto">
                <button onClick={() => mark(false)} className="px-4 py-3.5 rounded-2xl bg-gradient-to-r from-rose-600 to-rose-700 text-white font-display font-extrabold inline-flex items-center justify-center gap-2 shadow-lg hover:scale-105 active:scale-95 transition"
                    style={{ boxShadow: '0 8px 20px rgba(244,63,94,0.4), inset 0 2px 0 rgba(255,255,255,0.3)' }}
                    data-testid="flashcard-review-btn">
                    <X className="h-4 w-4" /> Review
                </button>
                <button onClick={() => mark(true)} className="px-4 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-700 text-white font-display font-extrabold inline-flex items-center justify-center gap-2 shadow-lg hover:scale-105 active:scale-95 transition"
                    style={{ boxShadow: '0 8px 20px rgba(16,185,129,0.4), inset 0 2px 0 rgba(255,255,255,0.3)' }}
                    data-testid="flashcard-gotit-btn">
                    <Check className="h-4 w-4" /> Got It
                </button>
            </div>

            {bursts.map((b) => <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />)}
            <AnnouncementBanner announce={announce} />
        </GameFrame>
    );
}

function Header({ muted, onToggleMute, idx, total, streak, bestStreak }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-700 flex items-center justify-center"
                style={{ boxShadow: '0 0 20px rgba(16,185,129,0.5)' }}>
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Flashcard Challenge</div>
                <div className="text-[11px] sm:text-xs text-slate-400 truncate">Master the lingo · build a streak</div>
            </div>
            <div className="flex gap-2">
                <ScoreTile label="Card" value={`${idx + 1}/${total}`} accent="from-sky-400 to-blue-500" />
                <ScoreTile label="Streak" value={`×${streak}`} accent="from-amber-400 to-yellow-500" />
            </div>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="flashcard-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}
